/**
 * PageSetupModal — page size, orientation and margins (File → Page setup…).
 *
 * Applying a change remounts the editor (the parent keys TipTapEditor on the
 * page-setup), repaginates, and persists via document meta.
 */

import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  PageSetup,
  PageSizeId,
  PAGE_SIZES,
  MARGIN_PRESETS,
  DEFAULT_PAGE_SETUP,
  normalizePageSetup,
} from "./utils/pageSetup";

interface PageSetupModalProps {
  show: boolean;
  onHide: () => void;
  initialSetup?: PageSetup | null;
  onApply: (setup: PageSetup) => void;
}

const MARGIN_FIELDS: Array<{
  key: keyof PageSetup["margins"];
  labelKey: string;
  fallback: string;
}> = [
  { key: "top", labelKey: "tiptapEditor.pageSetup.top", fallback: "Top" },
  {
    key: "bottom",
    labelKey: "tiptapEditor.pageSetup.bottom",
    fallback: "Bottom",
  },
  { key: "left", labelKey: "tiptapEditor.pageSetup.left", fallback: "Left" },
  { key: "right", labelKey: "tiptapEditor.pageSetup.right", fallback: "Right" },
];

const PageSetupModal: React.FC<PageSetupModalProps> = ({
  show,
  onHide,
  initialSetup,
  onApply,
}) => {
  const { t } = useTranslation();
  const [size, setSize] = useState<PageSizeId>(DEFAULT_PAGE_SETUP.size);
  const [orientation, setOrientation] = useState<PageSetup["orientation"]>(
    DEFAULT_PAGE_SETUP.orientation
  );
  const [margins, setMargins] = useState({ ...DEFAULT_PAGE_SETUP.margins });

  // Reset fields when the modal opens
  const prevShowRef = React.useRef(false);
  useEffect(() => {
    if (show && !prevShowRef.current) {
      const normalized = normalizePageSetup(initialSetup);
      setSize(normalized.size);
      setOrientation(normalized.orientation);
      setMargins({ ...normalized.margins });
    }
    prevShowRef.current = show;
  }, [show, initialSetup]);

  const handleApply = () => {
    onApply(normalizePageSetup({ size, orientation, margins }));
    onHide();
  };

  const setMargin = (key: keyof PageSetup["margins"], value: string) => {
    const parsed = parseFloat(value);
    setMargins(m => ({ ...m, [key]: isNaN(parsed) ? 0 : parsed }));
  };

  const matchingPreset =
    Object.keys(MARGIN_PRESETS).find(id => {
      const p = MARGIN_PRESETS[id].margins;
      return (
        p.top === margins.top &&
        p.right === margins.right &&
        p.bottom === margins.bottom &&
        p.left === margins.left
      );
    }) || "";

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16, fontWeight: 500 }}>
          {t("tiptapEditor.pageSetup.title", "Page setup")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Page size */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
            {t("tiptapEditor.pageSetup.pageSize", "Page size")}
          </Form.Label>
          <Form.Select
            value={size}
            onChange={e => setSize(e.target.value as PageSizeId)}
            style={{ fontSize: 13 }}
          >
            {(Object.keys(PAGE_SIZES) as PageSizeId[]).map(id => (
              <option key={id} value={id}>
                {PAGE_SIZES[id].label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Orientation */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
            {t("tiptapEditor.pageSetup.orientation", "Orientation")}
          </Form.Label>
          <div style={{ display: "flex", gap: 16 }}>
            <Form.Check
              type="radio"
              id="page-setup-portrait"
              name="page-orientation"
              label={t("tiptapEditor.pageSetup.portrait", "Portrait")}
              checked={orientation === "portrait"}
              onChange={() => setOrientation("portrait")}
              style={{ fontSize: 13 }}
            />
            <Form.Check
              type="radio"
              id="page-setup-landscape"
              name="page-orientation"
              label={t("tiptapEditor.pageSetup.landscape", "Landscape")}
              checked={orientation === "landscape"}
              onChange={() => setOrientation("landscape")}
              style={{ fontSize: 13 }}
            />
          </div>
        </Form.Group>

        {/* Margins */}
        <Form.Group>
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
            {t("tiptapEditor.pageSetup.margins", "Margins (inches)")}
          </Form.Label>
          <Form.Select
            value={matchingPreset}
            onChange={e => {
              const preset = MARGIN_PRESETS[e.target.value];
              if (preset) setMargins({ ...preset.margins });
            }}
            style={{ fontSize: 13, marginBottom: 10 }}
          >
            <option value="">
              {t("tiptapEditor.pageSetup.customMargins", "Custom")}
            </option>
            {Object.keys(MARGIN_PRESETS).map(id => (
              <option key={id} value={id}>
                {MARGIN_PRESETS[id].label}
              </option>
            ))}
          </Form.Select>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            {MARGIN_FIELDS.map(field => (
              <div key={field.key}>
                <Form.Label
                  style={{ fontSize: 12, color: "#5f6368", marginBottom: 2 }}
                >
                  {t(field.labelKey, field.fallback)}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={4}
                  step={0.05}
                  value={margins[field.key]}
                  onChange={e => setMargin(field.key, e.target.value)}
                  style={{ fontSize: 13 }}
                />
              </div>
            ))}
          </div>
        </Form.Group>

        <div
          style={{
            marginTop: 14,
            padding: "8px 12px",
            backgroundColor: "#f8f9fa",
            borderRadius: 6,
            fontSize: 13,
            color: "#5f6368",
          }}
        >
          {t(
            "tiptapEditor.pageSetup.hint",
            "Applies to the whole document and is used for DOCX export and printing. A4 is required for CNIPA/EPO filings."
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onHide} style={{ fontSize: 13 }}>
          {t("tiptapEditor.pageSetup.cancel", "Cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleApply}
          style={{ fontSize: 13 }}
        >
          {t("tiptapEditor.pageSetup.apply", "Apply")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PageSetupModal;
